import { useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { StreamEvent } from '@langchain/core/tracers/log_stream'
import { Toast } from '@base-ui-components/react/toast'

import { useChat, useIssues } from '@/lib/state'
import { receiveStream } from '@/lib/stream/client'
import Button from './ui/Button'
import { GitHubIssue } from '@/lib/github/types'
import { createNewThread } from '@/lib/thread/server'

const Issue = () => {
  const searchParams = useSearchParams()
  const { data: session, update } = useSession()
  const issueNumber = searchParams.get('issueNumber')
  const projectName = searchParams.get('projectName')
  const toastManager = Toast.useToastManager()
  const { issues, issuesLoading, setIssuesLoading } = useIssues()
  const issue: GitHubIssue | undefined = issues.find(
    i => i.number === parseInt(issueNumber ?? '')
  )
  const { addStreamEvent, setIsStreaming } = useChat()
  const project = session?.user?.projects.find(p => p.name === projectName)

  const solveIssue = async (issueNumber: number) => {
    setIssuesLoading({ [issueNumber]: true })

    const _thread = await createNewThread({
      projectId: project?.id as string,
      gitHubIssue: issueNumber
    }).then(thread => {
      if (thread) {
        const params = new URLSearchParams(searchParams)
        params.set('threadId', thread.id)
        window.history.pushState(null, '', `?${params.toString()}`)
        update()
      }
      return thread
    })

    receiveStream({
      body: {
        threadId: _thread.id,
        issueNumber,
        graphName: 'solveIssue'
      },
      onChatStream: (event: StreamEvent) => {
        const key =
          event.metadata.langgraph_node === 'tool'
            ? event.metadata.langgraph_node +
              ':' +
              event.metadata.langgraph_step
            : event.metadata.langgraph_node
        setIsStreaming(true)
        addStreamEvent(event, _thread.id, key)
      },
      doneCallback: _streamEvents => {
        setIsStreaming(false)
        setIssuesLoading({ [issueNumber]: false })
      },
      update,
      toastManager
    })
  }
  const disabled =
    !issue || issuesLoading[issue?.number as number] || issuesLoading.all

  return (
    <div className='p-4'>
      <div className='flex items-center place-content-between gap-2 mt-2 mb-6'>
        <h2 className='text-lg font-bold'>Issue #{issue?.number as number}</h2>
        <Button
          disabled={disabled}
          onClick={() => window.open(issue?.html_url, '_blank')}
        >
          View on GitHub
        </Button>
      </div>
      <h3 className='font-bold'>{issue?.title}</h3>
      <p className='text-sm text-gray-500 mt-4'>{issue?.body}</p>
      <div className='flex items-center gap-2 mt-2'>
        <Button>Refine</Button>
        <Button
          disabled={disabled}
          onClick={() => {
            solveIssue(issue?.number as number)
          }}
        >
          Solve
        </Button>
        <Button>Chat</Button>
      </div>
    </div>
  )
}

export default Issue
